import React, { useEffect, useRef, useState } from 'react'
import { ArrowDropDown } from '@mui/icons-material'
import {
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableBody,
  CircularProgress,
  Box,
  TableFooter,
  TableSortLabel,
  styled,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { DragDropContext, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd'
import { MthCheckbox } from '@mth/components/MthCheckbox'
import MthTableRow from '@mth/components/MthTable/MthTableRow'
import { mthTableClasses } from '@mth/components/MthTable/styles'
import { MthColor, Order, SortedStatus } from '@mth/enums'
import { convertWidth } from '@mth/utils/mthtable/mthtable.util'
import { MthTableProps, MthTableRowItem } from './types'

const CssTableSortLabel = styled(TableSortLabel)(() => ({
  '.MuiTableSortLabel-iconDirectionDesc': {
    color: `${MthColor.SYSTEM_01} !important`,
    fontSize: '22px',
    marginLeft: '16px',
    marginRight: '-40px',
  },
  '.MuiTableSortLabel-iconDirectionAsc': {
    color: `${MthColor.SYSTEM_01} !important`,
    fontSize: '22px',
    marginLeft: '16px',
    marginRight: '-40px',
  },
}))

// eslint-disable-next-line @typescript-eslint/ban-types
const MthTable = <T extends unknown>({
  fields,
  items,
  loading,
  selectable = false,
  showSelectAll = true,
  disableSelectAll = false,
  size = 'medium',
  checkBoxColor = 'primary',
  oddBg = true,
  borderBottom = true,
  isDraggable = false,
  defaultOrder = Order.DESC,
  defaultOrderBy = 'name',
  onArrange,
  onSelectionChange,
  onSortChange,
  onSelectionHandle,
  sx = [],
  isTableCellBorder,
  labelSize = 18,
  overFlowHidden,
  testId,
  noCellItemPadding,
}: MthTableProps<T>): React.ReactElement => {
  const [numSelected, setNumSelected] = useState<number>(0)
  const [rowCount, setRowCount] = useState<number>(0)
  const [tableWidth, setTableWidth] = useState<number>(0)
  const tableRef = useRef<HTMLDivElement>(null)
  const [order, setOrder] = useState<Order>(defaultOrder)
  const [orderBy, setOrderBy] = useState<string>(defaultOrderBy)

  const handleRequestSort = (fieldKey: string) => {
    const isAsc = orderBy === fieldKey && order === Order.ASC
    setOrder(isAsc ? Order.DESC : Order.ASC)
    setOrderBy(fieldKey)
    if (onSortChange) onSortChange(fieldKey, isAsc ? Order.DESC : Order.ASC)
  }

  const [lastSelectedIndex, setLastSelectedIndex] = React.useState<number | null>(null)

  const handleToggleCheck = (
    item: MthTableRowItem<T>,
    index?: number,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const isUnselect = item.isSelected

    if (event?.shiftKey && lastSelectedIndex !== null && index !== undefined) {
      const [start, end] = [lastSelectedIndex, index].sort((a, b) => a - b)

      // Toggle selection in range
      for (let i = start; i <= end; i++) {
        const wasSelected = items[i].isSelected
        items[i].isSelected = true
        if (onSelectionHandle && !wasSelected) onSelectionHandle(items[i], isUnselect)
      }
    } else {
      // Regular selection toggle
      item.isSelected = !item.isSelected
      if (onSelectionHandle) onSelectionHandle(item, isUnselect)
    }

    checkSelectedItems()
    handleSelectionChange(false)

    // Update last selected index
    if (index !== undefined) {
      setLastSelectedIndex(index)
    }
  }

  const handleToggleCheckAll = (checked: boolean) => {
    items.forEach((item) => {
      if (item.selectable !== false) {
        item.isSelected = checked
      }
    })

    checkSelectedItems()
    handleSelectionChange(true)
  }

  const checkSelectedItems = () => {
    setRowCount(items?.filter((item) => item.selectable !== false).length || 0)
    setNumSelected(items?.filter((item) => item.isSelected)?.length || 0)
  }

  const handleSelectionChange = (isAll: boolean) => {
    if (onSelectionChange) onSelectionChange(items, isAll)
  }

  const reorder = (list: MthTableRowItem<T>[], startIndex: number, endIndex: number) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return
    }
    if (onArrange) {
      const newItems = reorder(items, result.source.index, result.destination.index)
      onArrange(newItems)
    }
  }

  useEffect(() => {
    checkSelectedItems()
  }, [items])

  useEffect(() => {
    const handleWindowResize = () => {
      setTableWidth(tableRef.current?.clientWidth || 0)
    }
    handleWindowResize()
    window.addEventListener('resize', handleWindowResize)

    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  return (
    <div ref={tableRef}>
      <TableContainer>
        <Table
          {...(testId ? { 'data-testid': testId } : {})}
          sx={[
            mthTableClasses.table,
            ...(Array.isArray(sx) ? sx : [sx]),
            { overflow: overFlowHidden === true ? 'hidden' : 'unset' },
          ]}
          className={`${size} ${oddBg ? '' : 'noOddBg'} ${borderBottom && !oddBg ? '' : 'noBorderBottom'}`}
        >
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell className='checkWrap'>
                  <MthCheckbox
                    color='primary'
                    size={size}
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={rowCount > 0 && numSelected === rowCount}
                    onChange={(_e, checked) => handleToggleCheckAll(checked)}
                    sx={{ visibility: !showSelectAll ? 'hidden' : 'unset' }}
                    disabled={disableSelectAll}
                  />
                </TableCell>
              )}
              {fields.map((field) => (
                <TableCell
                  key={field.key}
                  width={convertWidth(field.width || 0, tableRef.current?.clientWidth || 0)}
                  sx={{ paddingX: noCellItemPadding ? '0 !important' : 'inherit', lineHeight: '1.3em' }}
                >
                  {field.sortable ? (
                    <CssTableSortLabel
                      active={true}
                      direction={orderBy === field.key ? order : Order.DESC}
                      onClick={() => handleRequestSort(field.key)}
                      IconComponent={ArrowDropDown}
                    >
                      <Box fontSize={labelSize}>{field.label}</Box>
                      {orderBy === field.key ? (
                        <Box component='span' sx={visuallyHidden}>
                          {order === Order.DESC ? SortedStatus.SORTED_DESCENDING : SortedStatus.SORTED_ASCENDING}
                        </Box>
                      ) : null}
                    </CssTableSortLabel>
                  ) : (
                    <Box fontSize={labelSize}>{field.label}</Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {!!isDraggable ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId='droppable'>
                {(provided: DroppableProvided) => (
                  <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                    {items.map((item, index) => (
                      <MthTableRow
                        key={`${item.key}`}
                        tableWidth={tableWidth}
                        index={index}
                        fields={fields}
                        item={item}
                        selectable={selectable}
                        isDraggable={isDraggable}
                        size={size}
                        checkBoxColor={checkBoxColor}
                        handleToggleCheck={handleToggleCheck}
                        noCellItemPadding={noCellItemPadding}
                      />
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <TableBody>
              {items.map((item, index) => (
                <MthTableRow
                  key={`${item.key}`}
                  tableWidth={tableWidth}
                  index={index}
                  fields={fields}
                  item={item}
                  selectable={selectable}
                  isDraggable={isDraggable}
                  size={size}
                  checkBoxColor={checkBoxColor}
                  handleToggleCheck={handleToggleCheck}
                  isTableCellBorder={isTableCellBorder}
                  noCellItemPadding={noCellItemPadding}
                />
              ))}
            </TableBody>
          )}
          {!items.length && Boolean(tableWidth) && (
            <TableFooter>
              <TableRow>
                <td colSpan={fields.length}>
                  {loading && (
                    <Box display={'flex'} justifyContent='center' py={1}>
                      <CircularProgress />
                    </Box>
                  )}
                </td>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </div>
  )
}
export default MthTable
